import { message } from "antd";
import { ClientError, GraphQLClient } from "graphql-request";
import type { DocumentNode } from "graphql";
import { Program } from "Interfaces/Program";
import { PROGRAMS_STATUS } from "constants/Program";
import { graphql } from "../__generated__/gql";
import { getCurrentTenantConfig, normalizeHost } from "../config/tenants";
import EARLY_RELEASE_CODE_DESCRIPTION, { LATE_ARRIVAL_CODE_DESCRIPTION } from "./constants";
import { breadcrumbGraphQL } from "./sentry";
import { useTenantSettingsStore } from "./tenantSettings/store";

// This is used to force the browser to reload the page when the version changes.
export const CURRENT_VERSION = "1.2.5";

const graphqlBaseUrl = () =>
  `${getCurrentTenantConfig().apiUrl || "http://localhost:4000"}/graphql`;

/**
 * ATT-2871 + ATT-2872: inject `X-Tenant-Domain` on every browser GraphQL
 * request, normalized via the same `normalizeHost()` the consolidated-backend
 * resolver (ATT-2866) uses, so the header value matches the registry lookup
 * key exactly (no trailing dot, no scheme, no port, lowercased).
 *
 * SSR no-op (no `window`); backend (ATT-2866) treats SSR requests as
 * internal calls per its trust model. Always-on (compat-only when
 * `useBackendSettings: false` on every tenant).
 *
 * Exported for unit testing. Type kept loose to avoid importing the
 * graphql-request internal `RequestMiddleware` type at every call site.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function tenantDomainRequestMiddleware<T extends { headers?: any }>(request: T): T {
  if (typeof window === "undefined") return request;
  // ATT-3226 GUARDRAIL: this header MUST stay `window.location.hostname` — the
  // REAL host — and MUST NOT be switched to `getCurrentHost()`. Under a staging
  // tenant override `getCurrentHost()` returns the simulated target; routing
  // the backend there would break the Option-A "branding-only" guarantee
  // (GraphQL must keep hitting the real staging tenant). Keep it literal.
  const host = normalizeHost(window.location.hostname);
  return {
    ...request,
    headers: {
      ...request.headers,
      "X-Tenant-Domain": host,
    },
  };
}

const rawGraphqlClient = new GraphQLClient(graphqlBaseUrl(), {
  credentials: "include",
  mode: "cors",
  requestMiddleware: tenantDomainRequestMiddleware,
});

function getOperationName(doc: unknown): string {
  if (doc && typeof doc === "object" && "definitions" in doc) {
    const defs = (doc as DocumentNode).definitions;
    const first = Array.isArray(defs) && defs[0] && "name" in defs[0] ? (defs[0] as { name?: { value?: string } }).name?.value : undefined;
    if (first) return first;
  }
  return "GraphQL";
}

const originalRequest = rawGraphqlClient.request.bind(rawGraphqlClient);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(rawGraphqlClient as any).request = async function requestWithBreadcrumb(document: any, ...args: any[]) {
  (rawGraphqlClient as any).url = graphqlBaseUrl();
  const operationName = getOperationName(document);
  breadcrumbGraphQL(operationName, args[0] != null ? { hasVariables: true } : undefined);
  // ATT-4508: an optional FOURTH positional argument carries an `AbortSignal`.
  // graphql-request@5 only accepts a signal through its object-form call, so
  // the wrapper switches form here; callers (the PIN kiosk's `kioskRequest`)
  // keep the positional `(document, variables)` shape every test mocks.
  const [variables, requestHeaders, signal] = args;
  if (signal) {
    return originalRequest({ document, variables, requestHeaders, signal } as any);
  }
  return originalRequest(document, variables, requestHeaders);
};

export const graphqlClient = rawGraphqlClient;

export function errorToStringUnfiltered(e: unknown): string {
  if (e instanceof ClientError)
    return (
      e.response.errors?.[0]?.message ??
      `GraphQL Error (Code: ${e.response.status})`
    );
  if (e instanceof Error) return e.message;
  if (
    typeof e === "object" &&
    e &&
    "message" in e &&
    typeof e.message === "string"
  )
    return e.message;
  return "Unknown Error";
}

export function errorToString(e: unknown): string {
  const errorString = errorToStringUnfiltered(e);
  if (errorString.toLowerCase().includes("econnreset"))
    return "Please retry soon (Connection Reset)";
  if (errorString.toLowerCase().includes("network error"))
    return "Please retry soon (Network Error)";
  if (errorString.toLowerCase().includes("failed to fetch"))
    return "Please retry soon (Failed to Fetch)";
  // ATT-4508: WebKit (Safari / iPad) reports a failed fetch as "Load failed" and
  // a dropped socket as "The network connection was lost"; without these two
  // branches a kiosk on an iPad printed the raw text where Chrome users got the
  // retry guidance above.
  if (
    errorString.toLowerCase().includes("load failed") ||
    errorString.toLowerCase().includes("network connection was lost")
  )
    return "Please retry soon (Network Error)";
  return errorString;
}

/**
 * Extract activeSessionIds from GraphQL error extensions.
 * Returns the first non-null session ID if available.
 *
 * Uses duck-typing instead of instanceof to handle cases where
 * bundle duplication or ESM/CJS interop causes instanceof checks to fail.
 */
export function extractActiveSessionIdFromError(e: unknown): string | undefined {
  // Duck-type check: verify the error has the expected shape
  // instead of using instanceof which can fail in production bundles
  if (!e || typeof e !== 'object') return undefined;

  const errorObj = e as Record<string, unknown>;
  const response = errorObj.response as Record<string, unknown> | undefined;
  if (!response || typeof response !== 'object') return undefined;

  const errors = response.errors as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(errors)) return undefined;

  // Find the first error that has activeSessionIds with a valid session ID
  const errorWithSessionId = errors.find((error) => {
    if (!error || typeof error !== 'object') return false;
    const extensions = error.extensions as Record<string, unknown> | undefined;
    const activeSessionIds = extensions?.activeSessionIds;
    return Array.isArray(activeSessionIds) && activeSessionIds.some(
      (id): id is string => typeof id === "string" && id !== null
    );
  });

  if (!errorWithSessionId) return undefined;

  const extensions = errorWithSessionId.extensions as Record<string, unknown> | undefined;
  const activeSessionIds = extensions?.activeSessionIds as unknown[];
  return activeSessionIds?.find(
    (id): id is string => typeof id === "string" && id !== null
  );
}

/**
 * Check if user has access to payments features
 * @param userEmail - User's email address
 * @returns boolean indicating if user has payments access
 */
export function hasPaymentsAccess(userEmail?: string | null): boolean {
  if (!userEmail) return false;
  return (
    userEmail === "aoseguera@romoland.net" ||
    userEmail === "alemus@romoland.net" ||
    userEmail === "eramos@romoland.net" ||
    userEmail === "lellefsen@romoland.net" ||
    userEmail.includes("@attendly.com")
  );
}

const WhoamiBaseDocument = graphql(`
  query WhoamiBase {
    whoami {
      id
      firstName
      lastName
      emailAddress
    }
  }
`);

// eslint-disable-next-line max-len
export async function getMyBaseInfo(): Promise<{
  id: string;
  emailAddress: string;
} | null> {
  const r1 = await graphqlClient.request(WhoamiBaseDocument);
  return r1.whoami || null;
}

const GRADE_NAMES: Record<number, string> = {
  [-10]: "",
  [-2]: "Preschool",
  [-1]: "Pre-Kindergarten",
  0: "Kindergarten",
  1: "1st Grade",
  2: "2nd Grade",
  3: "3rd Grade",
  4: "4th Grade",
  5: "5th Grade",
  6: "6th Grade",
  7: "7th Grade",
  8: "8th Grade",
  9: "9th Grade",
  10: "10th Grade",
  11: "11th Grade",
  12: "12th Grade",
};

const GRADE_SHORT_NAMES: Record<number, string> = {
  [-10]: "",
  [-2]: "PS",
  [-1]: "TK",
  0: "K",
  1: "1st",
  2: "2nd",
  3: "3rd",
  4: "4th",
  5: "5th",
  6: "6th",
  7: "7th",
  8: "8th",
  9: "9th",
  10: "10th",
  11: "11th",
  12: "12th",
};

export function intToGradeFullName(gradeInt: number): string {
  return GRADE_NAMES[gradeInt] || gradeInt.toString();
}

export function intToGradeShortName(gradeInt: number): string {
  return GRADE_SHORT_NAMES[gradeInt] || gradeInt.toString();
}

// Sorts grades numerically (NOT lexicographically) and renders them as a
// comma-separated list of short names, e.g. [10, 11, 12, 9] -> "9th, 10th, 11th, 12th".
// Does not mutate the input. Handles the PS/TK/K sentinels (-2, -1, 0) ahead of numbered grades.
export function formatGradesShortList(
  grades: number[] | null | undefined
): string {
  return [...(grades ?? [])]
    .sort((a, b) => a - b)
    .map((grade) => intToGradeShortName(grade))
    .join(", ");
}

export function getAllGradeNames(): { text: string; value: string }[] {
  return Object.keys(GRADE_SHORT_NAMES)
    .sort((a, b) => Number(a) - Number(b))
    .filter((key) => GRADE_SHORT_NAMES[Number(key)] !== "")
    .map((key) => ({
      text: GRADE_NAMES[Number(key)],
      value: GRADE_SHORT_NAMES[Number(key)],
    }));
}

export function intToGrade(gradeInt: number): string {
  if (gradeInt === 0) return "K";
  if (gradeInt === -1) return "TK";
  if (gradeInt === -2) return "PS";
  if (gradeInt === -10) return "";
  return gradeInt.toString();
}

export const getProgramStatus = (program: Program) => {
  const now = new Date();
  const currentWeekStart = new Date(now);
  currentWeekStart.setDate(now.getDate() - now.getDay()); // sunday
  currentWeekStart.setHours(0, 0, 0, 0);

  const currentWeekEnd = new Date(currentWeekStart);
  currentWeekEnd.setDate(currentWeekStart.getDate() + 6); // saturday
  currentWeekEnd.setHours(23, 59, 59, 999);

  if (program.isCanceled) {
    return PROGRAMS_STATUS.CANCELED.value;
  }

  const sessions = program.sessions || [];

  // `every` is vacuously true on an empty list, which tagged zero-session
  // programs as Past. They have no lifecycle status — same as the server
  // filter, which requires a session for Past/Future (ATT-4337).
  if (sessions.length === 0) return PROGRAMS_STATUS.NO_SESSIONS.value;

  const hasPast = sessions.every((s) => new Date(s.endTime) < currentWeekStart);

  const hasFuture = sessions.every(
    (s) => new Date(s.startTime) > currentWeekEnd
  );

  const hasCurrent = sessions.some((s) => {
    const start = new Date(s.startTime);
    const end = new Date(s.endTime);
    return start <= currentWeekEnd && end >= currentWeekStart;
  });

  // Check if there are some past sessions AND some future sessions (mixed case)
  const hasSomePast = sessions.some((s) => new Date(s.endTime) < currentWeekStart);
  const hasSomeFuture = sessions.some((s) => new Date(s.startTime) > currentWeekEnd);

  if (hasCurrent) return PROGRAMS_STATUS.CURRENT.value;
  if (hasPast) return PROGRAMS_STATUS.PAST.value;
  if (hasFuture) return PROGRAMS_STATUS.FUTURE.value;
  // If program has both past and future sessions (but none in current week), consider it CURRENT
  if (hasSomePast && hasSomeFuture) return PROGRAMS_STATUS.CURRENT.value;
  // Mixed case or undefined – optional fallback
  return PROGRAMS_STATUS.NO_SESSIONS.value;
};

export const formatDateTime = (isoString: string): string =>
  new Date(isoString).toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

// Excel auto-detects a cell as a datetime only when the value matches a
// recognised pattern with NO comma between the date and the time. The default
// `toLocaleString` output ("04/15/2026, 02:30:00 PM") trips this detection,
// so Excel stores the cell as text and sorting only orders by the date prefix.
// This helper produces the same MM/DD/YYYY HH:MM:SS AM/PM shape teachers are
// used to, but without the comma, and pins en-US so output is deterministic.
export const formatDateTimeForCsv = (isoString: string): string =>
  new Date(isoString)
    .toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
    .replace(",", "");

/**
 * Naming convention for the keys:
 * release-<feature>: to control the release of a feature, meant to be short lived
 * access-<feature>: to control access to functionality
 * custom-<flag>: to control access to custom functionality (like for SAUSD)
 */
export const allFeatureFlags = ["custom-sausd", "FF_AR_Attestation"] as const;

type FeatureFlag = (typeof allFeatureFlags)[number];

function getLocalStorageFlags(key: string): string[] {
  if (typeof window === "undefined") return [];
  const flags = localStorage.getItem(key);
  return flags ? flags.split(",").filter(Boolean) : [];
}

export function hasFeatureFlag(featureFlag: FeatureFlag): boolean {
  if (getLocalStorageFlags("featureFlags").includes(featureFlag)) {
    return true;
  }
  if (getLocalStorageFlags("featureFlagsDisabled").includes(featureFlag)) {
    return false;
  }
  // ATT-2871: read from the runtime store instead of bundled config directly.
  // The store is hydrated synchronously in `pages/_app.tsx` with bundled values
  // BEFORE any render — guaranteed to be valid (race-free). When
  // `useBackendSettings: true` for the tenant, the store is later updated by
  // the `useTenantSettings()` hook with backend-overridden flags.
  // Caveat (design.md §D-store): `hasFeatureFlag()` does NOT subscribe to
  // store updates — it's a one-shot read. Components that need to re-render
  // when flags change MUST use `useTenantSettings()` directly.
  const tenantFlags = useTenantSettingsStore.getState().featureFlags;
  const envFlags = process.env.NEXT_PUBLIC_FEATURE_FLAGS?.split(",").map((s) => s.trim()).filter(Boolean) ?? [];
  const environmentFeatureFlags = tenantFlags.length > 0 ? tenantFlags : envFlags;
  return environmentFeatureFlags.includes(featureFlag);
}

export const localDateTime = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleString();
};

export const localDate = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleDateString();
};

export const localTime = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

export const calculateDuration = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return endDate.getTime() - startDate.getTime();
};

export const formatDuration = (start: string, end: string) => {
  const diff = calculateDuration(start, end);
  const hours = Math.floor(diff / 1000 / 60 / 60);
  const minutes = Math.floor(diff / 1000 / 60) - hours * 60;
  return `${hours}h ${minutes}m`;
};

type AttendanceDurationInput = {
  startTime?: string | null;
  endTime?: string | null;
  checkInAuthorId?: string | null;
  programFlowStepId?: string | null;
  session?: { startTime?: string | null; endTime?: string | null } | null;
};

const resolveTimes = (
  attendance: AttendanceDurationInput
): { start: string; end: string } | null => {
  // A Program Flow-managed row (has a stamped step id) represents a REAL interval:
  // the sweep opens each return-to-Core stint at the club boundary and closes it
  // when the student leaves again. It carries no checkInAuthorId, so without this
  // it would fall to the session branch and show the whole session length for
  // every stint (over-counting Core ADA). Use the row's own times.
  if (
    (attendance.checkInAuthorId || attendance.programFlowStepId) &&
    attendance.startTime &&
    attendance.endTime
  ) {
    return { start: attendance.startTime, end: attendance.endTime };
  }
  if (!attendance.checkInAuthorId && attendance.session?.startTime && attendance.session?.endTime) {
    return { start: attendance.session.startTime, end: attendance.session.endTime };
  }
  return null;
};

/**
 * Resolves the display duration for an attendance record.
 *
 * - Kiosk records (checkInAuthorId set): uses actual check-in/out timestamps.
 * - Auto-created records (no checkInAuthorId): falls back to session scheduled times.
 * - Returns "" when no valid timestamps can be resolved.
 */
export const resolveAttendanceDuration = (attendance: AttendanceDurationInput): string => {
  const times = resolveTimes(attendance);
  return times ? formatDuration(times.start, times.end) : "";
};

/**
 * Returns the resolved duration in milliseconds for sorting purposes.
 * Uses the same resolution logic as resolveAttendanceDuration.
 */
export const resolveAttendanceDurationMs = (attendance: AttendanceDurationInput): number => {
  const times = resolveTimes(attendance);
  return times ? calculateDuration(times.start, times.end) : 0;
};

export const userDisplayName = (
  user:
    | {
        firstName: string;
        lastName: string;
        emailAddress?: string | null;
      }
    | null
    | undefined,
  userDependentRelationship?: {
    parentFirstName?: string | null;
    parentLastName?: string | null;
  } | null
) => {
  // First, try to use metadata from userDependentRelationship if available
  if (userDependentRelationship) {
    // If we have both parentFirstName and parentLastName in metadata, show both
    if (userDependentRelationship.parentFirstName && userDependentRelationship.parentLastName) {
      return `${userDependentRelationship.parentFirstName} ${userDependentRelationship.parentLastName}`;
    }
    // If we have only parentFirstName in metadata, show it
    if (userDependentRelationship.parentFirstName) {
      return userDependentRelationship.parentFirstName;
    }
    // If we have only parentLastName in metadata, show it
    if (userDependentRelationship.parentLastName) {
      return userDependentRelationship.parentLastName;
    }
  }

  // If metadata is not available or doesn't have names, fall back to user object
  if (!user) {
    return "";
  }
  // If we have both firstName and lastName, show both
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  // If we have firstName but no lastName, show firstName
  if (user.firstName) {
    return user.firstName;
  }
  // If we have lastName but no firstName, show lastName
  if (user.lastName) {
    return user.lastName;
  }
  // If we have email but no firstName/lastName, show email
  if (user.emailAddress) {
    return user.emailAddress;
  }
  // Fallback to empty string if nothing is available
  return "";
};

export const cleanName = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^a-z0-9]/g, "");

/**
 * ATT-3242: build a URL-safe slug from a human-readable name (e.g. a program
 * session title) so it can be embedded in the URL as `?session=<slug>`.
 * Lowercases, strips diacritics, collapses every run of non-alphanumeric
 * characters into a single dash, and trims leading/trailing dashes.
 */
export const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const buildLoginCookieURL = async (
  record: { emailAddress: string; id: string; phoneNumber?: string | null },
  userId: string,
  role: string
): Promise<string> => {
  // Local dev: the tenant config bootstraps publicBase as https://localhost,
  // which nothing serves — use the real (http) origin so the login-cookie
  // route on this same app is reachable. NODE_ENV-gated: never active in
  // production builds or tests.
  const configuredBase =
    getCurrentTenantConfig().publicBase || process.env.NEXT_PUBLIC_BASE || window.location.origin;
  const baseUrl =
    process.env.NODE_ENV === "development" && window.location.hostname === "localhost"
      ? window.location.origin
      : configuredBase;
  const { hostname } = new URL(baseUrl);
  const domain = hostname.startsWith("admin.")
    ? hostname.slice(hostname.indexOf(".") + 1)
    : hostname;

  // Get the admin token from our API with current user info
  const response = await fetch("/api/generate-admin-token", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ userId, role }),
  });
  if (!response.ok) {
    throw new Error("Failed to generate admin token");
  }
  const { token: adminToken } = await response.json();

  let personalInfo = "";
  if (record.emailAddress) {
    personalInfo = `email=${encodeURIComponent(record.emailAddress)}`;
  } else if (record.phoneNumber) {
    personalInfo = `phone=${encodeURIComponent(record.phoneNumber)}`;
  } else {
    throw new Error("No email address or phone number found for this user.");
  }

  return `${baseUrl}/api/login-cookie?${personalInfo}&id=${
    record.id
  }&domain=${domain}&adminToken=${encodeURIComponent(adminToken)}`;
};

export const handleCopyLoginCookieURL = async (
  record: { emailAddress: string; id: string; phoneNumber?: string | null },
  userId: string,
  role: string
) => {
  try {
    const url = await buildLoginCookieURL(record, userId, role);
    await navigator.clipboard.writeText(url);
    message.success("Parent login URL copied — open in incognito to impersonate");
  } catch (error: unknown) {
    if (error instanceof Error) {
      message.error(`Failed to copy URL, error: ${error.message}`);
    } else {
      message.error("Failed to copy URL");
    }
  }
};

// ATT-3311: same login-cookie URL as the copy handler, opened directly in a
// new tab. The blank tab is opened SYNCHRONOUSLY (before the async token
// fetch) — window.open after an await gets blocked by popup heuristics in
// Safari/Chrome.
export const handleImpersonateLoginCookieURL = async (
  record: { emailAddress: string; id: string; phoneNumber?: string | null },
  userId: string,
  role: string
) => {
  const impersonationTab = window.open("", "_blank");
  try {
    const url = await buildLoginCookieURL(record, userId, role);
    if (impersonationTab) {
      impersonationTab.location.href = url;
    } else {
      // Popup blocked despite the synchronous open — fall back to clipboard.
      await navigator.clipboard.writeText(url);
      message.warning("Popup blocked — parent login URL copied instead");
    }
  } catch (error: unknown) {
    impersonationTab?.close();
    if (error instanceof Error) {
      message.error(`Failed to impersonate, error: ${error.message}`);
    } else {
      message.error("Failed to impersonate");
    }
  }
};

export const isoStringToDate = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleDateString([], {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

export const isoStringToTime = (isoString: string) => {
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const mapEarlyReleaseCode = (
  code: string | null | undefined,
  customCodes: { code: string; description: string }[]
) => {
  if (!code) return "";
  return (
    EARLY_RELEASE_CODE_DESCRIPTION[
      code as keyof typeof EARLY_RELEASE_CODE_DESCRIPTION
    ] ||
    customCodes?.find((c) => c.code === code)?.description ||
    "Other"
  );
};

export const mapLateArrivalCode = (
  code: string | null | undefined,
  customCodes: { code: string; description: string }[]
) => {
  if (!code) return "";
  return (LATE_ARRIVAL_CODE_DESCRIPTION[code as keyof typeof LATE_ARRIVAL_CODE_DESCRIPTION] || customCodes?.find((c) => c.code === code)?.description || "Other");
};

/**
 * Format a birth date string avoiding timezone conversion issues.
 * Handles both YYYY-MM-DD and ISO string formats.
 */
export const formatBirthDate = (dateStr: string): string => {
  try {
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;

    // Parse as date-only to avoid timezone conversion
    const [year, month, day] = datePart.split('-').map(Number);

    if (!year || !month || !day) {
      return "N/A";
    }

    const date = new Date(year, month - 1, day);
    // Verify no rollover occurred
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return "N/A";
    }

    return Number.isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
  } catch (error) {
    return "N/A";
  }
};

export const isAttendanceLockActive = (
  sessionStartTime: string | undefined,
  lockAttendanceInHours: number | undefined
): boolean => {
  if (!sessionStartTime || lockAttendanceInHours === undefined || lockAttendanceInHours < 0) {
    return false;
  }

  const startTime = new Date(sessionStartTime);

  if (Number.isNaN(startTime.getTime())) {
    return false;
  }

  const lockCutoff = new Date(
    startTime.getTime() + lockAttendanceInHours * 60 * 60 * 1000
  );

  return new Date() > lockCutoff;
};

/** Substring to detect API rejection when attendance edit is locked (no active exception). */
export const ATTENDANCE_LOCK_ERROR_PATTERN = "Attendance is locked";

/** User-facing message when API rejects edit due to lock (no exception). */
export const ATTENDANCE_LOCK_USER_MESSAGE =
  "Editing for this date is no longer allowed. If you need to make changes, ask support to create an attendance edit exception for this program and date.";

/**
 * Tooltip shown on disabled Edit / Delete attendance buttons when the row's
 * session is past the lock window AND no active edit exception exists.
 * Canonical string — see openspec/changes/add-locked-attendance-controls-and-session-list-indicator/design.md.
 */
export const ATTENDANCE_EDIT_LOCKED_TOOLTIP =
  "Attendance edits are locked for this session date. Ask your Super District Admin to create an edit exception.";

export function isAttendanceLockError(error: unknown): boolean {
  const msg = errorToString(error);
  return msg.includes(ATTENDANCE_LOCK_ERROR_PATTERN);
}

export function formatPhoneNumber(phone: string | null | undefined): string {
  const cleaned = `${phone ?? ""}`.replace(/\D/g, "")
  const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/)
  if (match) return `+1 (${match[1]}) ${match[2]}-${match[3]}`
  return phone || ""
}

// Program utility functions
export const allGrades = Array.from({ length: 15 }, (_, i) => i - 2);

export const formatDate = (isoDateString: string | undefined) => {
  if (!isoDateString) return "";
  const date = new Date(isoDateString);
  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return formatter.format(date);
};

export const formatTime = (isoDateString: string | null) => {
  if (!isoDateString) return "";
  const date = new Date(isoDateString);
  const formatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return formatter.format(date);
};

export const formatDateOnly = (dateStr: string | null | undefined): string => {
  if (!dateStr) {
    return "N/A";
  }

  try {
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;

    // Parse as date-only to avoid timezone conversion
    const [year, month, day] = datePart.split('-').map(Number);

    if (!year || !month || !day) {
      return "N/A";
    }

    const date = new Date(year, month - 1, day);
    // Verify no rollover occurred
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return "N/A";
    }

    return Number.isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        });
  } catch (error) {
    return "N/A";
  }
};
